import { Cfg } from './config.js';

/**
 * Génère un token anti-bot pour l'action donnée, dispatché vers le provider
 * actif (reCAPTCHA v3).
 *
 * Résout à `{ provider, token }` où `provider` est 'none'/'recaptcha' et
 * `token` est la chaîne signée (ou '' si non dispo).
 *
 * Résout TOUJOURS — jamais reject. Si le widget n'est pas chargé (adblock,
 * réseau, etc.), renvoie un token vide. Le backend fait fail-open quand
 * le provider n'est pas configuré, donc un token vide n'est pas un blocker
 * en dev. En prod avec provider configuré, le backend 403 sur token vide.
 *
 * v1.1.58 — Cloudflare Turnstile retiré.
 *
 * @param {string} action  Nom de l'action (ex. 'submit', 'save_contact').
 * @return {Promise<{provider:string, token:string}>}
 */
export function botToken( action ) {
	var provider = Cfg.botProvider || 'none';
	var siteKey  = Cfg.botSiteKey || '';

	if ( provider === 'none' || ! siteKey || typeof window === 'undefined' ) {
		return Promise.resolve( { provider: 'none', token: '' } );
	}

	if ( provider === 'recaptcha' ) {
		if ( ! window.grecaptcha ) {
			return Promise.resolve( { provider: provider, token: '' } );
		}
		return new Promise( function ( resolve ) {
			try {
				window.grecaptcha.ready( function () {
					try {
						window.grecaptcha.execute( siteKey, { action: action } )
							.then( function ( t ) { resolve( { provider: 'recaptcha', token: t || '' } ); } )
							.catch( function () { resolve( { provider: 'recaptcha', token: '' } ); } );
					} catch ( e ) { resolve( { provider: 'recaptcha', token: '' } ); }
				} );
			} catch ( e ) { resolve( { provider: 'recaptcha', token: '' } ); }
		} );
	}

	return Promise.resolve( { provider: 'none', token: '' } );
}

// Compat ascendante : ancien helper qui ne renvoyait que le token reCAPTCHA.
// Préservé pour ne pas casser un code qui l'importerait. Nouveau code utilise
// botToken() qui dispatche au bon provider.
export function recaptchaToken( action ) {
	return botToken( action ).then( function ( r ) {
		return r.provider === 'recaptcha' ? r.token : '';
	} );
}

export function apiUrl( path, query ) {
	var base = Cfg.restUrl.replace( /\/+$/, '' ) + '/' + path.replace( /^\/+/, '' );
	if ( ! query ) return base;
	var parts = [];
	for ( var k in query ) {
		if ( Object.prototype.hasOwnProperty.call( query, k ) && query[ k ] != null ) {
			parts.push( encodeURIComponent( k ) + '=' + encodeURIComponent( query[ k ] ) );
		}
	}
	return base + ( parts.length ? '?' + parts.join( '&' ) : '' );
}

export function apiGet( path, query ) {
	return fetch( apiUrl( path, query ), {
		credentials: 'same-origin',
		headers: { 'X-WP-Nonce': Cfg.nonce || '' },
	} ).then( function ( r ) { return r.json(); } );
}

export function apiPost( path, body, opts ) {
	opts = opts || {};
	var ctrl = ( typeof AbortController === 'function' ) ? new AbortController() : null;
	var timeoutMs = opts.timeoutMs || 45000; // 45 s couvre l'API Anthropic (~25-30s) avec marge
	var timer = null;
	if ( ctrl ) {
		timer = setTimeout( function () { ctrl.abort(); }, timeoutMs );
	}

	// Injecte le token anti-bot pour les endpoints qui le demandent. Le
	// caller passe `opts.recaptchaAction` (nom historique) ou `opts.botAction`
	// pour activer. On dispatch vers le provider actif via botToken() et
	// on attache le token sous la clé `recaptcha_token` attendue par
	// `Bot_Protection::guard_rest`.
	var action = opts.botAction || opts.recaptchaAction || '';
	var bodyPromise = action
		? botToken( action ).then( function ( res ) {
			var b = body || {};
			if ( res.token && res.provider === 'recaptcha' ) {
				b.recaptcha_token = res.token;
			}
			return b;
		} )
		: Promise.resolve( body || {} );

	return bodyPromise.then( function ( finalBody ) {
		return fetch( apiUrl( path ), {
			method: 'POST',
			credentials: 'same-origin',
			headers: {
				'Content-Type': 'application/json',
				'X-WP-Nonce': Cfg.nonce || '',
			},
			body: JSON.stringify( finalBody ),
			signal: ctrl ? ctrl.signal : undefined,
		} )
			.then( function ( r ) {
				if ( timer ) clearTimeout( timer );
				return r.json();
			} )
			.catch( function ( e ) {
				if ( timer ) clearTimeout( timer );
				var aborted = e && e.name === 'AbortError';
				return Promise.reject( aborted ? new Error( 'timeout' ) : e );
			} );
	} );
}
